
import { useTheme, MenuItem } from '@mui/material';
import { CustomMessage } from 'components/core';
import { connect, useDispatch } from 'react-redux';
import { CancellationRoot, CancellationHintText, CancellationTitle, ReasonTextField, StyledBottomButtons, StyledSecondaryButton, StyledMainButton, CancellationCustomMessage } from './styles';
import { scriptMessagesSet, cancelStepSet, successStepSet } from 'store/steps';
import { useOnlineBookingApi } from 'shell';
import clsx from 'clsx';
import { useAnalyticsEventPageView } from 'screens/hooks';
import { useReducer } from 'hooks';
import { isObjectEmpty } from 'utils';
import { messageType } from 'assets/constants';

// Function to count the number of options
const countOptions = (cancelationReasons) => {
  const numberOfReasons = cancelationReasons.length;
  const noOptions = numberOfReasons === 0;
  const oneOption = numberOfReasons === 1;
  const manyOptions = numberOfReasons > 1;
  return [noOptions, oneOption, manyOptions]
}

// Function to merge cancelation reasons if active and translations
const mergeCancelationReasons = (stepCancelation, orgCancelation) =>
  orgCancelation.filter((orgObj) =>
    !!stepCancelation.find(cancelationId =>
      orgObj.isActive && orgObj.appointmentCancellationReasonId === cancelationId));


// CancelAppointment component
const CancelAppointment = ({ step, appointmentData, closePopup, cancelStepSet, successStepSet, cancelationReasons, userData, isAppointmentDetails = false }) => {
  useAnalyticsEventPageView("Cancel popup")

  const theme = useTheme();
  const api = useOnlineBookingApi();
  const cancelApi = api.appointment.useCancelAppointment();

  const { cancelationReasonsIds, hasRecaptcha, errorMessage: errorText, title, additionalDescription, label, buttonBack, buttonCancelAppointment, placeholder } = step;
  const dispatch = useDispatch();

  const { journeyGUID, processGUID, identity } = userData;
  // Merge and count the cancelation reasons
  const reasonsOptions = mergeCancelationReasons(cancelationReasonsIds, cancelationReasons);
  const [noOptions, oneOption, manyOptions] = countOptions(reasonsOptions);

  // State variables to manage the selected reason and error state
  const [{ reasonId, cancelErrorActive, submitDisabled, scriptMessages, errorMessage }, updateState] = useReducer({
    reasonId: oneOption ? reasonsOptions[0].appointmentCancellationReasonId : '',
    cancelErrorActive: false,
    errorMessage: errorText,
    scriptMessages: [],
    submitDisabled: false
  });

  // Function to handle the cancel appointment action
  const cancelAppointment = async () => {
    try {
      updateState({ submitDisabled: true })
      const res = await cancelApi.mutateAsync({
        processGuid: appointmentData?.processGuid || processGUID,
        cancelationReasonId: reasonId,
        hasRecaptcha: hasRecaptcha,
        journeyGUID: isAppointmentDetails ? identity : journeyGUID
      })

      if (!!res) {
        cancelStepSet(true)
        successStepSet(true);
        if (res.scriptMessages?.length)
          dispatch(scriptMessagesSet(res.scriptMessages))
      }

      closePopup();
    } catch (error) {
      const { errorMessage, scriptMessages } = error || {};
      updateState({ cancelErrorActive: true, errorMessage: errorText || errorMessage, scriptMessages })
    }
  }

  // if we have a reason or there is no options do not disable the submit
  const disableSubmit = !(reasonId || noOptions);

  return (
    <CancellationRoot>
      <>
        <CancellationTitle>
          {title}
        </CancellationTitle>
        <CancellationHintText>
          {additionalDescription}
        </CancellationHintText>
        <CancellationCustomMessage>
          {scriptMessages?.map(({ message, messageType }, index) => (<CustomMessage key={index} message={message} type={messageType} />))}
          {cancelErrorActive && !scriptMessages?.length && <CustomMessage message={errorMessage} type={messageType.error} />}
        </CancellationCustomMessage>
      </>


      {manyOptions && !cancelErrorActive && (
        <ReasonTextField
          fullWidth
          className={clsx({ 'selected': reasonId })}
          label={label}
          value={reasonId}
          onChange={e => { updateState({ reasonId: e.target.value }) }}
          InputLabelProps={{ shrink: true }}
          SelectProps={{
            displayEmpty: true,
            renderValue: () => {
              const element = reasonsOptions?.find(({ appointmentCancellationReasonId }) => reasonId === appointmentCancellationReasonId)
              const value = element?.publicName || element?.name || placeholder;
              return <div title={value}>{value}</div>
            }
          }}
        >

          {
            reasonsOptions.map((option) => (
              <MenuItem key={option.appointmentCancellationReasonId} value={option.appointmentCancellationReasonId} title={option?.publicName || option?.name}>
                {option?.publicName || option?.name}
              </MenuItem>
            ))
          }

        </ReasonTextField>
      )}

      <StyledBottomButtons>
        <StyledSecondaryButton size='small' onClick={closePopup} title={buttonBack}>
          {buttonBack}
        </StyledSecondaryButton>
        <StyledMainButton size='small' type='submit' disabled={submitDisabled || disableSubmit || cancelErrorActive} onClick={() => { cancelAppointment(); }} title={buttonCancelAppointment}>
          {buttonCancelAppointment}
        </StyledMainButton>
      </StyledBottomButtons>

    </CancellationRoot>
  );
}

export default connect(
  ({ current: { steps: { appointmentData }, userData } }, componentProps) =>
  ({
    userData,
    appointmentData: (isObjectEmpty(appointmentData) ? componentProps.appointmentData : appointmentData)
  }),
  { cancelStepSet, successStepSet })
  (CancelAppointment);